import moment from 'moment-timezone';
import { Store } from "../../reducers/store";
import { getBookingList, getConfig, setBookingList, setCurrentBooking } from "../../reducers/configSlice";
import { fetchDeviceBookingList } from "../bookingList";
import { isSoketiConnected } from "./soketi";
import { BOOKING_INTERVAL_CONFIG } from "./constants";
import { dispatchAction } from "../state/dispatchAction";

let bookingIntervalId = null;
let isProcessing = false;
let lastCheckedDate = null;

export const initSoketiBookingInterval = () => {
    if (bookingIntervalId) {
        return;
    }

    if (!isSoketiConnected()) {
        return;
    }

    bookingIntervalId = setInterval(() => {
        checkBookingBoundaries();
    }, BOOKING_INTERVAL_CONFIG.CHECK_INTERVAL_MS);

    // Run initial check
    checkBookingBoundaries();
};

export const stopSoketiBookingInterval = () => {
    if (bookingIntervalId) {
        clearInterval(bookingIntervalId);
        bookingIntervalId = null;
        isProcessing = false;
        lastCheckedDate = null; // Reset date tracking when stopping
    }
};

const checkBookingBoundaries = async () => {
    if (isProcessing) {
        return;
    }

    if (!isSoketiConnected()) {
        stopSoketiBookingInterval();
        return;
    }

    try {
        isProcessing = true;
        
        const state = Store.getState();
        const config = getConfig(state);
        const bookingList = getBookingList(state);

        if (!config || !config.timezone || bookingList?.length === 0) {
            return;
        }

        const currentMoment = moment().tz(config.timezone);
        const currentDate = currentMoment.format('YYYY-MM-DD');

        let hasStateChanges = false;

        // Check if date has changed (midnight crossed) - refetch booking list for new day
        if (lastCheckedDate && lastCheckedDate !== currentDate) {
            await fetchDeviceBookingList();
            lastCheckedDate = currentDate;
            hasStateChanges = true;
            return; // Skip other checks this cycle, let the new data settle
        }

        // Set initial date if not set
        if (!lastCheckedDate) {
            lastCheckedDate = currentDate;
        }

        // Check if current booking has ended
        const currentBooking = bookingList.find(booking => booking.current);
        if (currentBooking) {
            const endMoment = moment.tz(currentBooking.end, config.timezone);
            if (currentMoment.isAfter(endMoment) || currentMoment.isSame(endMoment)) {
                await fetchDeviceBookingList();
                hasStateChanges = true;
            }
        }

        // Check if any booking should start now (become current) - only if no current booking ended
        if (!hasStateChanges) {
            const shouldBeCurrentBooking = bookingList.find(booking => {
                if (booking.current) return false; // Skip already current bookings

                const startMoment = moment.tz(booking.start, config.timezone);
                const endMoment = moment.tz(booking.end, config.timezone);

                // Check if current time is within booking time (start <= now < end)
                return currentMoment.isBetween(startMoment, endMoment, undefined, '[)');
            });

            if (shouldBeCurrentBooking) {
                await fetchDeviceBookingList();
                hasStateChanges = true;
            }
        }

        // Check for early checkin opportunities - MANUAL STATE UPDATE (no API fetch)
        if (!hasStateChanges && config.granularity <= 15 && config.has_checkin) {
            const shouldBeEarlyBooking = bookingList.find(booking => {
                if (booking.current || booking.early) return false; // Skip current or already early bookings

                const startMoment = moment.tz(booking.start, config.timezone);
                const earlyStartMoment = startMoment.clone().subtract(BOOKING_INTERVAL_CONFIG.EARLY_CHECKIN_DURATION, 'minutes');

                // Check if current time is within early checkin window
                return currentMoment.isBetween(earlyStartMoment, startMoment, undefined, '[)');
            });

            if (shouldBeEarlyBooking) {
                updateForEarlyBooking(shouldBeEarlyBooking, bookingList);
                hasStateChanges = true;
            }
        }

        // Check if early checkin window has ended (booking should become current) - only if no other changes
        if (!hasStateChanges) {
            const earlyBookingThatShouldStart = bookingList.find(booking => {
                if (!booking.early) return false; // Only check early bookings

                const startMoment = moment.tz(booking.start, config.timezone);

                // Check if booking start time has arrived
                return currentMoment.isAfter(startMoment) || currentMoment.isSame(startMoment);
            });

            if (earlyBookingThatShouldStart) {
                await fetchDeviceBookingList();
                hasStateChanges = true;
            }
        }
    } catch (error) {
        console.error('[SOKETI_BOOKING_INTERVAL] Error checking booking boundaries:', error);
    } finally {
        isProcessing = false;
    }
};

const updateForEarlyBooking = (earlyBooking, currentBookingList) => {
    try {
        // Create updated booking list - mark the early booking as current+early, clear other current bookings
        const newBookingList = currentBookingList.map(booking => {
            if (booking.booking_id === earlyBooking.booking_id) {
                // Mark this booking as current and early (same as useBookingListInterval)
                return { ...booking, current: true, early: true, next: false };
            } else if (booking.current) {
                // Remove current flag from any other bookings
                return { ...booking, current: false };
            } else {
                return booking;
            }
        });

        // Create the current booking object (same as useBookingListInterval)
        const currentBookingObject = { ...earlyBooking, current: true, early: true, next: false };

        // Update Redux store (same as useBookingListInterval)
        dispatchAction(setBookingList(newBookingList));
        dispatchAction(setCurrentBooking(currentBookingObject));
    } catch (error) {
        console.error('[SOKETI_BOOKING_INTERVAL] Error updating early booking state:', error);
    }
};

/**
 * Check if the Soketi booking interval is currently running
 */
export const isSoketiBookingIntervalRunning = () => {
    return bookingIntervalId !== null;
};

export default {
    initSoketiBookingInterval,
    stopSoketiBookingInterval,
    isSoketiBookingIntervalRunning
};
